import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  TypedTerminal, Sparkline, Terminal,
  PipeRule, StdoutStream, SAMPLE_ROWS,
} from '../components/prototype-ui'
import { PricingTierCard, type PricingTier } from '../components/ui/PricingTierCard'

const SCENARIO_META = [
  { label: 'au-housing/sales-q2',      cols: ['region', 'sku', 'units', 'revenue', 'trend'] },
  { label: 'you/competitor-research',  cols: ['company', 'product', 'pricing', 'share', 'trend'] },
  { label: 'you/orders',               cols: ['order_id', 'status', 'amount', 'customer', 'trend'] },
  { label: 'you/prod-errors',          cols: ['service', 'level', 'count', 'rate', 'trend'] },
  { label: 'you/q2-report',            cols: ['region', 'category', 'revenue', 'growth', 'trend'] },
]

// Realistic rows per scenario — 4 rows × 4 data cells (sparkline is always col 5)
const SCENARIO_ROWS: [string, string, string, string][][] = [
  // sales-q2
  [
    ['AU',    'SKU-1042', '1,240',  '$19,840'],
    ['US',    'SKU-2188', '4,820',  '$96,400'],
    ['DE',    'SKU-0445', '318',    '$7,950'],
    ['JP',    'SKU-2311', '902',    '$18,040'],
  ],
  // competitor-research
  [
    ['OpenAI',     'GPT-4o',      '$0.005/tok', '38.2%'],
    ['Anthropic',  'Claude 3.5',  '$0.003/tok', '22.7%'],
    ['Google',     'Gemini 1.5',  '$0.002/tok', '18.4%'],
    ['Mistral',    'Large 2',     '$0.004/tok', '11.3%'],
  ],
  // orders
  [
    ['ORD-8821', 'shipped',    '$1,240', 'acme-corp'],
    ['ORD-8820', 'pending',    '$380',   'globex-inc'],
    ['ORD-8819', 'delivered',  '$4,200', 'initech'],
    ['ORD-8818', 'processing', '$890',   'umbrella'],
  ],
  // prod-errors
  [
    ['api-gateway',   'ERROR', '1,842', '2.3%'],
    ['auth-svc',      'WARN',  '423',   '0.5%'],
    ['db-proxy',      'ERROR', '91',    '0.1%'],
    ['queue-worker',  'FATAL', '12',    '0.01%'],
  ],
  // q2-report
  [
    ['APAC',   'SaaS',       '$2.4M', '+18%'],
    ['AMER',   'Enterprise', '$5.1M', '+31%'],
    ['EMEA',   'SMB',        '$1.8M', '+12%'],
    ['LATAM',  'Startup',    '$0.6M', '+44%'],
  ],
]

const HERO_SCENARIOS = [
  [
    { kind: 'cmd' as const, text: "node scrape.js | instadash push --name sales-q2", speed: 18, pause: 500 },
    { kind: 'out' as const, text: "✓ 42,180 rows · 8 cols inferred", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ live at instadash.io/you/sales-q2", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ embed ready", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ indexed in mesh", cls: 'ok', speed: 6, pause: 2200 },
  ],
  [
    { kind: 'cmd' as const, text: "agent run research-task | instadash push --name competitor-research", speed: 16, pause: 500 },
    { kind: 'out' as const, text: "✓ 1,240 rows · 12 cols inferred", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ live at instadash.io/you/competitor-research", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ embed ready", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ indexed in mesh", cls: 'ok', speed: 6, pause: 2200 },
  ],
  [
    { kind: 'cmd' as const, text: "curl https://api.example.com/orders | instadash push", speed: 16, pause: 500 },
    { kind: 'out' as const, text: "✓ 8,300 rows · 6 cols inferred", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ live at instadash.io/you/orders", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ embed ready", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ indexed in mesh", cls: 'ok', speed: 6, pause: 2200 },
  ],
  [
    { kind: 'cmd' as const, text: "cat logs.ndjson | instadash push --name prod-errors", speed: 18, pause: 500 },
    { kind: 'out' as const, text: "✓ 94,002 rows · 4 cols inferred", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ live at instadash.io/you/prod-errors", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ embed ready", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ indexed in mesh", cls: 'ok', speed: 6, pause: 2200 },
  ],
  [
    { kind: 'cmd' as const, text: "cat export.csv | instadash push --name q2-report", speed: 18, pause: 500 },
    { kind: 'out' as const, text: "✓ 3,500 rows · 9 cols inferred", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ live at instadash.io/you/q2-report", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ embed ready", cls: 'ok', speed: 6, pause: 200 },
    { kind: 'out' as const, text: "✓ indexed in mesh", cls: 'ok', speed: 6, pause: 2200 },
  ],
]

function Hero() {
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const initialScenario = useState(() => Math.floor(Math.random() * HERO_SCENARIOS.length))[0]
  const [scenarioIdx, setScenarioIdx] = useState(initialScenario)

  function handleCopy() {
    navigator.clipboard.writeText('npm i -g instadash').catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <section className="hero-section" style={{ padding: '72px 0 96px', borderBottom: '1px solid var(--line-soft)', position: 'relative', overflow: 'hidden' }}>
      <StdoutStream enabled={true} />
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'linear-gradient(to right, rgba(27,31,36,0.97) 0%, rgba(27,31,36,0.95) 50%, rgba(27,31,36,0.55) 75%, rgba(27,31,36,0.1) 100%)',
      }} />
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="resp-grid-hero" style={{ gap: 64, alignItems: 'start' }}>
          <div>
            <h1 className="h-display">
              The hosted <span className="accent" style={{ whiteSpace: 'nowrap' }}>data surface</span><br/>
              for AI agents.
            </h1>
            <p className="lede" style={{ marginTop: 20, fontSize: 18, maxWidth: 480 }}>
              Stop making agents rebuild fragile tables in chat. Give your agents one endpoint to publish research, logs, and scraped data into a live, persistent, and embeddable data grid.<br/>
              <span style={{ color: 'var(--fg-3)', fontSize: 15 }}>No database. No frontend. No schema setup.</span>
            </p>
            <div className="row gap-12" style={{ marginTop: 28 }}>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/get-started')}>
                Create your first grid
              </button>
              <button className="btn btn-ghost btn-lg" onClick={() => navigate('/showcase/mcp-servers-2026')}>
                View live demo
              </button>
            </div>
            <div className="hide-mobile" style={{ marginTop: 14 }}>
              <code
                className="mono"
                onClick={handleCopy}
                style={{
                  fontSize: 12, color: copied ? 'var(--lime)' : 'var(--fg-3)', cursor: 'pointer',
                  background: 'var(--bg-2)', padding: '5px 12px', borderRadius: 4,
                  border: '1px solid var(--line-soft)', display: 'inline-block',
                  transition: 'color 0.2s', userSelect: 'all',
                }}
              >
                {copied ? '✓ copied' : '$ npm i -g instadash'}
              </code>
            </div>
            <div style={{ marginTop: 18, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {['MCP-native', 'Token-efficient', 'Human-in-the-loop'].map(t => (
                <span key={t} style={{ fontSize: 12, color: 'var(--fg-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ color: 'var(--lime)' }}>✓</span> {t}
                </span>
              ))}
            </div>
          </div>

          <div className="col gap-12">
            <TypedTerminal scenarios={HERO_SCENARIOS} title="~/projects/au — bash" onScenarioChange={setScenarioIdx} initialScenario={initialScenario} />
            <MiniGridPreview scenarioIdx={scenarioIdx} />
          </div>
        </div>
      </div>
    </section>
  )
}

function MiniGridPreview({ scenarioIdx = 0 }: { scenarioIdx?: number }) {
  const [, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1400)
    return () => clearInterval(t)
  }, [])
  const meta = SCENARIO_META[scenarioIdx % SCENARIO_META.length]
  const scenarioRows = SCENARIO_ROWS[scenarioIdx % SCENARIO_ROWS.length]
  const sparks = SAMPLE_ROWS.slice(0, 4)
  return (
    <div className="card" style={{ width: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>instadash · {meta.label}</span>
        <span className="chip chip-lime" style={{ marginLeft: 'auto', fontSize: 10 }}>
          <span className="dot"></span>live
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--mono)', fontSize: 11 }}>
        <thead>
          <tr style={{ background: 'var(--bg-2)', color: 'var(--fg-3)' }}>
            {meta.cols.map(c => (
              <th key={c} style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 500, borderBottom: '1px solid var(--line-soft)' }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {scenarioRows.map((cells, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--line-soft)', animation: `pulse 1.4s ${i * 0.12}s infinite` }}>
              <td style={{ padding: '6px 10px', color: 'var(--fg-1)' }}>{cells[0]}</td>
              <td style={{ padding: '6px 10px', color: 'var(--fg-2)' }}>{cells[1]}</td>
              <td style={{ padding: '6px 10px', color: 'var(--fg)' }}>{cells[2]}</td>
              <td style={{ padding: '6px 10px', color: 'var(--lime)' }}>{cells[3]}</td>
              <td style={{ padding: '6px 10px', width: 80 }}>
                <Sparkline data={sparks[i].spark} color="var(--accent)" height={14} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.92}}`}</style>
    </div>
  )
}

function TwoPillars() {
  return (
    <section className="section">
      <div className="container">
        <div className="eyebrow"><span className="num">06</span>two surfaces</div>
        <h2 className="h1" style={{ marginTop: 14 }}>One push. Two surfaces.</h2>
        <p className="lede" style={{ marginTop: 12, marginBottom: 36, fontSize: 15, color: 'var(--fg-2)', maxWidth: 560 }}>
          Your agent finds it. You review it. Your agent acts on it.
        </p>

        <div className="resp-grid-2" style={{ gap: 16 }}>
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="eyebrow" style={{ color: 'var(--accent)' }}>GRID · FOR HUMANS</div>
            <h3 className="h2" style={{ fontSize: 24 }}>See the data.</h3>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
              The review surface for agent output. Search, filter, and inspect rows — then hand back to your agent with confidence.
            </p>
            <ul className="col gap-8" style={{ listStyle: 'none', marginTop: 4 }}>
              {['Search and filter agent output', 'Deep links to share exact views', 'Track every agent push with Time Machine', 'Schema inferred automatically'].map(f => (
                <li key={f} className="row gap-8" style={{ fontSize: 13, color: 'var(--fg-1)' }}>
                  <span className="mono" style={{ color: 'var(--accent)', fontSize: 11 }}>›</span>{f}
                </li>
              ))}
            </ul>
            <div style={{ padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--line-soft)', borderRadius: 6, marginTop: 'auto' }}>
              <pre className="mono" style={{ fontSize: 12, color: 'var(--accent)' }}>{`cat data.json | instadash push`}</pre>
            </div>
          </div>

          <div className="card card-pad" style={{
            display: 'flex', flexDirection: 'column', gap: 18,
            borderColor: 'rgba(183,157,255,0.3)',
            background: 'linear-gradient(180deg, rgba(183,157,255,0.04) 0%, transparent 100%), var(--bg-1)',
          }}>
            <div className="eyebrow" style={{ color: '#B79DFF' }}>AGENT · FOR LLMS</div>
            <h3 className="h2" style={{ fontSize: 24 }}>Reuse the data.</h3>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
              Every grid exposes API endpoints and LLM-ready context. Agents push data in, humans review it, agents query it back — all from the same URL.
            </p>
            <ul className="col gap-8" style={{ listStyle: 'none', marginTop: 4 }}>
              {['llms.txt and llms.md on every grid', 'Queryable API endpoints', 'MCP-native: push and read via tool calls', 'Token-efficient — data lives outside the chat context'].map(f => (
                <li key={f} className="row gap-8" style={{ fontSize: 13, color: 'var(--fg-1)' }}>
                  <span className="mono" style={{ color: '#B79DFF', fontSize: 11 }}>›</span>{f}
                </li>
              ))}
            </ul>
            <div style={{ padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--line-soft)', borderRadius: 6, marginTop: 'auto' }}>
              <pre className="mono" style={{ fontSize: 12, color: '#B79DFF' }}>{`agent.tool('instadash_push', { data: rows })`}</pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}


function ProblemStatement() {
  const navigate = useNavigate()
  return (
    <section className="section" style={{ borderBottom: '1px solid var(--line-soft)' }}>
      <div className="container">
        <div style={{ marginBottom: 40 }}>
          <div className="eyebrow"><span className="num">01</span>the problem</div>
          <h2 className="h2" style={{ marginTop: 14, fontSize: 42, lineHeight: 1.1, letterSpacing: '-0.025em', fontWeight: 700 }}>Agents shouldn't render grids.<br/>Your context window isn't a spreadsheet.</h2>
          <p className="lede" style={{ marginTop: 16, fontSize: 16, color: 'var(--fg-2)', lineHeight: 1.65 }}>
            AI agents generate structured data constantly, but displaying it inside a chat window is slow, wasteful, and ephemeral. Markdown tables get truncated, formatting breaks, and the data disappears when the session ends. Instadash gives that data a permanent home that both humans and agents can use.
          </p>
          <button
            onClick={() => navigate('/agents')}
            className="mono"
            style={{
              marginTop: 14, background: 'transparent', border: 'none',
              color: 'var(--accent)', fontSize: 13, cursor: 'pointer', padding: 0,
            }}
          >
            Read the four-part manifesto →
          </button>
        </div>

        <div className="resp-grid-2" style={{ gap: 16, alignItems: 'stretch' }}>
          {/* BEFORE */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--err, #ff6b6b)', letterSpacing: '0.12em' }}>BEFORE — agent renders in chat</div>
            <div className="card" style={{ flex: 1, borderColor: 'rgba(255,100,100,0.2)' }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>Claude — conversation</span>
              </div>
              <div style={{ padding: 16 }}>
                <div className="mono" style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 12 }}>Here's the comparison you asked for:</div>
                <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 4 }}>
                  <pre className="mono" style={{ fontSize: 11, color: 'var(--fg-2)', lineHeight: 1.65, margin: 0, whiteSpace: 'pre' }}>{`| Company  | Revenue | Growth | Cap    | Mo...
|----------|---------|--------|--------|------
| Acme     | $2.4M   | +18%   | $120M  | [tr…
| Globex   | $5.1M   | +31%   | $340M  | [tr…
| Initech  | $1.8M   | +12%   | $89M   | [tr…
| Umbrella | $0.6M   | +44%   | $210M  | [tr…`}</pre>
                  <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 72, background: 'linear-gradient(to right, transparent, var(--bg-1))' }} />
                </div>
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--err, #ff6b6b)' }}>⚠ Table truncated — 4 columns hidden</span>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--fg-4)' }}>Session will clear on refresh.</span>
                </div>
              </div>
            </div>
          </div>

          {/* AFTER */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--lime)', letterSpacing: '0.12em' }}>AFTER — agent publishes to Instadash</div>
            <div className="card" style={{ flex: 1, borderColor: 'rgba(184,255,58,0.15)' }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line-soft)' }}>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>~/projects — bash</span>
              </div>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-soft)' }}>
                <div className="mono" style={{ fontSize: 12, lineHeight: 2 }}>
                  <div><span style={{ color: 'var(--accent)' }}>$</span> <span style={{ color: 'var(--fg-1)' }}>instadash push --name comparison</span></div>
                  <div><span style={{ color: 'var(--lime)' }}>✓</span> <span style={{ color: 'var(--fg-2)' }}>10 rows · 8 cols inferred</span></div>
                  <div><span style={{ color: 'var(--lime)' }}>✓</span> <span style={{ color: 'var(--fg-2)' }}>persistent · queryable · private</span></div>
                  <div><span style={{ color: 'var(--lime)' }}>✓</span> <span style={{ color: 'var(--accent)' }}>instadash.io/you/comparison</span></div>
                </div>
              </div>
              <iframe
                src="https://instadash.io/test-builder/comparison?embed=true"
                style={{ width: '100%', minHeight: 220, border: 'none', borderRadius: '0 0 6px 6px', display: 'block' }}
                title="Live grid — MCP Tools comparison"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  const steps = [
    { n: '01', t: 'PIPE',  d: 'Send JSON, CSV, NDJSON, logs, or agent output from anywhere.', code: 'cat data.json | instadash push' },
    { n: '02', t: 'INFER', d: 'Columns, types, dates, links, images, and numbers are detected automatically.', code: 'schema inferred' },
    { n: '03', t: 'VIEW',  d: 'A live grid URL — humans can search and filter, agents can query via MCP or API.', code: 'instadash.io/you/sales-q2' },
    { n: '04', t: 'USE',   d: 'Every push ships llms.txt, llms.md, a queryable API, and a versioned snapshot. One endpoint, reusable forever.', code: 'llms.txt · llms.md · /api · Time Machine' },
  ]
  return (
    <section className="section">
      <div className="container">
        <div className="eyebrow"><span className="num">02</span>pipeline</div>
        <h2 className="h1" style={{ marginTop: 14 }}>From agent output to live grid in seconds.</h2>
        <p className="lede" style={{ marginTop: 16, marginBottom: 36, maxWidth: 560, color: 'var(--fg-2)', fontSize: 16 }}>
          Your agent produces JSON. One push creates a persistent grid, queryable API, and LLM context — accessible to humans and agents alike.
        </p>

        <div className="resp-grid-4" style={{ gap: 16 }}>
          {steps.map((s, i) => {
            const stepColor = i === 0 ? 'var(--accent)' : i === 3 ? 'var(--lime)' : `color-mix(in oklch, var(--accent) ${75 - i * 20}%, var(--lime))`
            return (
              <div key={s.n} style={{ background: 'var(--bg-1)', borderRadius: 8, border: '1px solid var(--line-soft)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: 2, background: stepColor }} />
                <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <span className="mono" style={{ color: stepColor, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>{s.n}</span>
                    {i < 3 && <span style={{ color: 'var(--fg-4)', fontSize: 16 }}>→</span>}
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--fg)', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 10 }}>{s.t}</div>
                  <p style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.6, marginBottom: 20, flex: 1 }}>{s.d}</p>
                  <div style={{ background: 'var(--bg)', borderRadius: 5, border: '1px solid var(--line-soft)', overflow: 'hidden' }}>
                    <div style={{ padding: '4px 10px', borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: stepColor, display: 'inline-block', opacity: 0.7 }} />
                      <span className="mono" style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>output</span>
                    </div>
                    <code className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', display: 'block', padding: '8px 10px', wordBreak: 'break-all', lineHeight: 1.5 }}>{s.code}</code>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

type SnippetTab = 'curl' | 'python' | 'node' | 'bash' | 'mcp'

function LiveDemo() {
  const [tab, setTab] = useState<SnippetTab>('mcp')
  const snippets: Record<SnippetTab, string> = {
    curl: `curl -X POST https://instadash.io/ingest \\
  -H "Authorization: Bearer sk_…" \\
  -H "Content-Type: application/json" \\
  -H "X-Grid-Name: sales-q2" \\
  --data-binary @data.json`,
    python: `import requests

r = requests.post(
    "https://instadash.io/ingest",
    headers={
        "Authorization": "Bearer sk_…",
        "X-Grid-Name": "sales-q2",
        "Content-Type": "application/json",
    },
    json=rows,
)
print(r.json()["grid_url"])`,
    node: `const res = await fetch("https://instadash.io/ingest", {
  method: "POST",
  headers: {
    "Authorization": "Bearer sk_…",
    "X-Grid-Name": "sales-q2",
    "Content-Type": "application/json",
  },
  body: JSON.stringify(rows),
});
const { grid_url } = await res.json();`,
    bash: `cat data.json | instadash push --name sales-q2`,
    mcp: `// claude_desktop_config.json
"instadash": {
  "command": "npx",
  "args": ["-y", "instadash-mcp"],
  "env": { "INSTADASH_KEY": "sk_…" }
}`,
  }

  return (
    <section className="section">
      <div className="container">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, flexWrap: 'wrap', gap: 24 }}>
          <div>
            <div className="eyebrow"><span className="num">03</span>integrations</div>
            <h2 className="h1" style={{ marginTop: 14 }}>Give your agents a real data surface.</h2>
            <p className="lede" style={{ marginTop: 12, fontSize: 15, color: 'var(--fg-2)', maxWidth: 480 }}>
              Built for MCP workflows. Agents publish structured data and return a live grid URL — queryable, persistent, embeddable.
            </p>
          </div>
          <div className="row gap-4" style={{ background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 6, padding: 4 }}>
            {(['mcp', 'curl', 'python', 'node', 'bash'] as SnippetTab[]).map(k => (
              <button key={k} onClick={() => setTab(k)} className="mono"
                style={{
                  padding: '6px 12px', fontSize: 12, borderRadius: 4,
                  background: tab === k ? 'var(--bg-3)' : 'transparent',
                  color: tab === k ? 'var(--fg)' : 'var(--fg-2)',
                  border: 'none', cursor: 'pointer', fontWeight: 500,
                }}>{k}</button>
            ))}
          </div>
        </div>

        <div className="resp-grid-hero" style={{ gap: 24, alignItems: 'stretch' }}>
          <Terminal title={`one-liner — ${tab}`}>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, lineHeight: 1.7, color: 'var(--fg-1)' }}>{snippets[tab]}</pre>
          </Terminal>
          <ResponseCard />
        </div>

        {/* MCP Tools */}
        <div style={{ marginTop: 32 }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', letterSpacing: '0.12em', marginBottom: 12 }}>MCP TOOLS</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {['instadash_push', 'instadash_read', 'instadash_list', 'instadash_get_schema', 'instadash_list_versions'].map(t => (
              <span key={t} className="mono" style={{
                fontSize: 11, color: 'var(--accent)',
                background: 'var(--bg-1)', border: '1px solid var(--line-soft)',
                borderRadius: 4, padding: '4px 10px',
              }}>{t}</span>
            ))}
          </div>
        </div>

        {/* Recommended behaviour callout */}
        <div style={{
          marginTop: 24,
          padding: '20px 24px',
          background: 'var(--bg-1)',
          border: '1px solid var(--line-soft)',
          borderLeft: '3px solid var(--accent)',
          borderRadius: '0 6px 6px 0',
        }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--accent)', letterSpacing: '0.12em', marginBottom: 10 }}>RECOMMENDED AGENT BEHAVIOUR</div>
          <p className="mono" style={{ fontSize: 12, color: 'var(--fg-2)', lineHeight: 1.7 }}>
            When an agent needs to show structured data, search results, scraped output, logs, comparisons, extracted entities, or research tables — publish the data to Instadash and return the live grid URL instead of rendering a large table in chat.
          </p>
        </div>
      </div>
    </section>
  )
}

function ResponseCard() {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="row" style={{ padding: '10px 16px', borderBottom: '1px solid var(--line-soft)', justifyContent: 'space-between' }}>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>HTTP 200 · response.json</span>
        <span className="chip chip-lime" style={{ fontSize: 10 }}>
          <span className="dot"></span>87 ms
        </span>
      </div>
      <pre className="mono" style={{ padding: 18, fontSize: 12, lineHeight: 1.8, color: 'var(--fg-1)', flex: 1, overflowX: 'auto' }}>
{`{
  `}<span style={{ color: '#6FAEFF' }}>"status"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"processing"</span>{`,
  `}<span style={{ color: '#6FAEFF' }}>"handle"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"au-housing"</span>{`,
  `}<span style={{ color: '#6FAEFF' }}>"slug"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"sales-q2"</span>{`,
  `}<span style={{ color: '#6FAEFF' }}>"version"</span>{`: `}<span style={{ color: 'var(--warn)' }}>1</span>{`,
  `}<span style={{ color: '#6FAEFF' }}>"resources"</span>{`: {
    `}<span style={{ color: '#6FAEFF' }}>"grid"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"instadash.io/au-housing/sales-q2"</span>{`,
    `}<span style={{ color: '#6FAEFF' }}>"embed"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"instadash.io/embed/sales-q2"</span>{`,
    `}<span style={{ color: '#6FAEFF' }}>"api"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"instadash.io/au-housing/sales-q2/rows"</span>{`,
    `}<span style={{ color: '#6FAEFF' }}>"llms_txt"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"instadash.io/au-housing/sales-q2/llms.txt"</span>{`,
    `}<span style={{ color: '#6FAEFF' }}>"llms_md"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"instadash.io/au-housing/sales-q2/llms.md"</span>{`,
    `}<span style={{ color: '#6FAEFF' }}>"mcp"</span>{`: `}<span style={{ color: 'var(--lime)' }}>"instadash://grids/au-housing/sales-q2"</span>{`
  }
}`}
      </pre>
    </div>
  )
}

const EMBED_BASE = 'https://instadash.io/test-builder/comparison'

function EmbedSection() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark')
  const [compact, setCompact] = useState(false)
  const [inspector, setInspector] = useState(false)
  const [search, setSearch] = useState('')

  const params = new URLSearchParams({ embed: 'true', theme })
  if (compact) params.set('compact', 'true')
  if (inspector) params.set('inspector', 'true')
  if (search.trim()) params.set('search', search.trim())
  const embedSrc = `${EMBED_BASE}?${params}`
  const snippetUrl = `${EMBED_BASE}?${params}`

  const toggleStyle = (active: boolean) => ({
    padding: '5px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
    fontFamily: 'var(--mono)', fontWeight: 500, border: '1px solid var(--line-soft)',
    background: active ? 'var(--accent)' : 'var(--bg-1)',
    color: active ? 'var(--bg)' : 'var(--fg-2)',
    transition: 'all 0.15s',
  } as React.CSSProperties)

  return (
    <section className="section">
      <div className="container">
        <div className="eyebrow"><span className="num">04</span>embeddable</div>
        <h2 className="h1" style={{ marginTop: 14 }}>A live grid inside any workflow.</h2>
        <p className="lede" style={{ marginTop: 12, marginBottom: 32, fontSize: 15, color: 'var(--fg-2)', maxWidth: 560 }}>
          Every grid includes an embed URL. Drop it into internal tools, docs, portals, or dashboards.
        </p>

        {/* Param toggles */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
          <span className="mono" style={{ fontSize: 10, color: 'var(--fg-4)', letterSpacing: '0.1em', marginRight: 4 }}>THEME</span>
          <button style={toggleStyle(theme === 'dark')} onClick={() => setTheme('dark')}>dark</button>
          <button style={toggleStyle(theme === 'light')} onClick={() => setTheme('light')}>light</button>
          <span style={{ width: 1, height: 20, background: 'var(--line-soft)', margin: '0 4px' }} />
          <button style={toggleStyle(compact)} onClick={() => setCompact(c => !c)}>compact</button>
          <button style={toggleStyle(inspector)} onClick={() => setInspector(i => !i)}>inspector</button>
          <span style={{ width: 1, height: 20, background: 'var(--line-soft)', margin: '0 4px' }} />
          <input
            className="mono"
            placeholder="search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              fontSize: 11, padding: '5px 10px', borderRadius: 4,
              background: 'var(--bg-1)', border: '1px solid var(--line-soft)',
              color: 'var(--fg)', outline: 'none', width: 140,
            }}
          />
        </div>

        {/* Live iframe */}
        <div className="card" style={{ overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>instadash · test-builder/comparison · live embed</span>
            <span className="chip chip-lime" style={{ fontSize: 10 }}><span className="dot"></span>live</span>
          </div>
          <iframe
            key={embedSrc}
            src={embedSrc}
            style={{ width: '100%', height: 420, border: 'none', display: 'block' }}
            title="instadash embed demo"
          />
        </div>

        {/* Generated code */}
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--line-soft)' }}>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-4)', letterSpacing: '0.1em' }}>EMBED CODE</span>
          </div>
          <pre className="mono" style={{ padding: '14px 16px', fontSize: 12, lineHeight: 1.7, color: 'var(--fg-2)', margin: 0, overflowX: 'auto' }}>
{`<iframe\n  src="`}<span style={{ color: 'var(--lime)' }}>{snippetUrl}</span>{`"\n  width="100%" height="420" frameborder="0"\n></iframe>`}
          </pre>
        </div>
      </div>
    </section>
  )
}

function FirecrawlLogo({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C9.5 5 7 7.5 7 11c0 2.76 2.24 5 5 5s5-2.24 5-5c0-1.5-.6-2.9-1.6-3.9C14.6 8.5 14 10 13 11c-.28-2.1-1-4-1-9z" fill="#FF6C37"/>
      <path d="M12 16c-2.76 0-5-2.24-5-5 0 3.31 2.69 6 6 6s6-2.69 6-6c0 2.76-2.24 5-5 5h-2z" fill="#FF8C57" opacity="0.6"/>
    </svg>
  )
}

type TermColor = 'comment' | 'cmd' | 'step' | 'ok' | 'url' | 'vendor'

interface HomepageRecipe {
  id:           string         // /recipes/<id>
  vendor:       string         // display name in the header chip
  vendorColor:  string         // chip color
  vendorBg:     string         // header gradient tint (rgba)
  vendorBorder: string         // card border tint (rgba)
  showFirecrawlLogo?: boolean  // only Firecrawl has a real SVG
  vendorTag:    string         // line under the terminal eyebrow, e.g. 'firecrawl + instadash · bash'
  title:        string
  blurb:        string
  chain:        Array<{ label: string; color: string }>
  ctaText:      string
  terminal:     Array<{ text: string; c: TermColor }>
}

const HOMEPAGE_RECIPES: HomepageRecipe[] = [
  {
    id: 'firecrawl-to-instadash',
    vendor: 'Firecrawl',
    vendorColor: '#FF6C37',
    vendorBg: 'rgba(255,108,55,0.05)',
    vendorBorder: 'rgba(255,108,55,0.2)',
    showFirecrawlLogo: true,
    vendorTag: 'firecrawl + instadash · bash',
    title: 'From web data to live grid.',
    blurb: 'Firecrawl gets agents access to the web. Instadash gives them somewhere to put what they find. One pipeline, no glue.',
    chain: [
      { label: 'Firecrawl crawl',    color: '#FF6C37' },
      { label: 'Push to Instadash',  color: 'var(--accent)' },
      { label: 'Live grid',          color: 'var(--lime)' },
      { label: 'Indexed in mesh',    color: '#B79DFF' },
    ],
    ctaText: 'Read the Firecrawl recipe →',
    terminal: [
      { text: '# crawl any domain, push typed rows', c: 'comment' },
      { text: '$ npx tsx crawl-hn.ts',               c: 'cmd' },
      { text: '',                                    c: 'comment' },
      { text: '→ firecrawl: starting crawl of news.ycombinator.com', c: 'step' },
      { text: '→ extracted 28 rows',                 c: 'step' },
      { text: '→ instadash: pushing to grid hn-top', c: 'step' },
      { text: '✓ done · v1',                         c: 'ok' },
      { text: '  instadash.io/your-handle/hn-top',   c: 'url' },
    ],
  },
  {
    id: 'claude-mcp-instadash',
    vendor: 'Claude + MCP',
    vendorColor: 'var(--accent)',
    vendorBg: 'rgba(0,229,255,0.05)',
    vendorBorder: 'var(--accent-line)',
    vendorTag: 'claude desktop · mcp',
    title: 'Claude → Instadash in 30 seconds.',
    blurb: 'Paste one URL into Claude.ai, Cursor, or Windsurf. Your grids appear in the tool list. Seven tools, OAuth, zero config.',
    chain: [
      { label: 'Paste MCP URL', color: 'var(--accent)' },
      { label: 'OAuth sign-in', color: '#B79DFF' },
      { label: 'Tools appear',  color: 'var(--lime)' },
      { label: 'Agent runs',    color: '#FFB347' },
    ],
    ctaText: 'Read the Claude + MCP recipe →',
    terminal: [
      { text: '# paste this URL into your client',     c: 'comment' },
      { text: '> https://mcp.instadash.io',            c: 'url' },
      { text: '',                                      c: 'comment' },
      { text: '✓ instadash_list      list workspace grids',     c: 'ok' },
      { text: '✓ instadash_push      create / update a grid',   c: 'ok' },
      { text: '✓ instadash_read      paginate rows',            c: 'ok' },
      { text: '✓ instadash_edits     read HITL human edits',    c: 'ok' },
      { text: '✓ instamesh_search    search the public mesh',   c: 'ok' },
    ],
  },
  {
    id: 'langgraph-instadash',
    vendor: 'LangGraph',
    vendorColor: '#F26B21',
    vendorBg: 'rgba(242,107,33,0.05)',
    vendorBorder: 'rgba(242,107,33,0.2)',
    vendorTag: 'langgraph · python',
    title: 'Agent pauses, human edits, agent continues.',
    blurb: 'LangGraph publishes mid-graph output to an editable grid, interrupts for human review, and reads the picks back before continuing.',
    chain: [
      { label: 'Agent generates',  color: '#F26B21' },
      { label: 'Publish to grid',  color: 'var(--accent)' },
      { label: 'Human ticks',      color: 'var(--lime)' },
      { label: 'Agent reads back', color: '#B79DFF' },
    ],
    ctaText: 'Read the LangGraph recipe →',
    terminal: [
      { text: '# durable agent + durable data',      c: 'comment' },
      { text: '$ python agent.py --thread-id demo',  c: 'cmd' },
      { text: '',                                    c: 'comment' },
      { text: '→ generated 5 candidates',            c: 'step' },
      { text: '→ published to grid · interrupting…', c: 'step' },
      { text: '  review here:',                      c: 'step' },
      { text: '  instadash.io/your-handle/competitor-shortlist', c: 'url' },
      { text: '  then re-run with --thread-id demo', c: 'step' },
    ],
  },
  {
    id: 'crewai-instadash',
    vendor: 'CrewAI',
    vendorColor: '#FFB347',
    vendorBg: 'rgba(255,179,71,0.05)',
    vendorBorder: 'rgba(255,179,71,0.25)',
    vendorTag: 'crewai · python',
    title: 'Typed crew output, persistent surface.',
    blurb: "CrewAI's output_pydantic gives you validated rows. One HTTP push lands them in a grid your team can actually look at.",
    chain: [
      { label: 'Researcher',   color: '#FFB347' },
      { label: 'Analyst',      color: '#FF8A65' },
      { label: 'Typed output', color: 'var(--accent)' },
      { label: 'Live grid',    color: 'var(--lime)' },
    ],
    ctaText: 'Read the CrewAI recipe →',
    terminal: [
      { text: '# two agents, one validated push',         c: 'comment' },
      { text: '$ python crew.py',                         c: 'cmd' },
      { text: '',                                         c: 'comment' },
      { text: '→ [Researcher] working…',                  c: 'step' },
      { text: '→ [Analyst] validating against schema…',   c: 'step' },
      { text: '→ crew produced 5 competitors',            c: 'step' },
      { text: '✓ pushed · v1',                            c: 'ok' },
      { text: '  instadash.io/your-handle/competitors',   c: 'url' },
    ],
  },
  {
    id: 'pydanticai-instadash',
    vendor: 'PydanticAI',
    vendorColor: '#E92063',
    vendorBg: 'rgba(233,32,99,0.05)',
    vendorBorder: 'rgba(233,32,99,0.25)',
    vendorTag: 'pydantic-ai · python',
    title: 'Type-safe agent, live CRM.',
    blurb: 'A Pydantic schema, a typed agent, and a select-column action spec. The dashboard becomes a CRM-lite the moment rows land.',
    chain: [
      { label: 'Pydantic schema', color: '#E92063' },
      { label: 'Agent generates', color: 'var(--accent)' },
      { label: 'Action column',   color: 'var(--lime)' },
      { label: 'Live grid',       color: '#B79DFF' },
    ],
    ctaText: 'Read the PydanticAI recipe →',
    terminal: [
      { text: '# type-safe agent → live CRM',           c: 'comment' },
      { text: '$ python agent.py',                      c: 'cmd' },
      { text: '',                                       c: 'comment' },
      { text: '→ generating leads for ICP',             c: 'step' },
      { text: '→ produced 8 validated leads',           c: 'step' },
      { text: '✓ pushed · v1',                          c: 'ok' },
      { text: '  instadash.io/your-handle/lead-shortlist', c: 'url' },
      { text: '  action: outreach (pending → sent)',    c: 'step' },
    ],
  },
  {
    id: 'langchain-mesh-rag',
    vendor: 'LangChain',
    vendorColor: '#1C8B8B',
    vendorBg: 'rgba(28,139,139,0.05)',
    vendorBorder: 'rgba(28,139,139,0.25)',
    vendorTag: 'langchain + instamesh · python',
    title: 'RAG without a vector DB.',
    blurb: 'Plug instamesh_search as a LangChain tool. The agent retrieves from public grids with built-in citations — no embedding pipeline.',
    chain: [
      { label: 'Agent query',     color: '#1C8B8B' },
      { label: 'instamesh_search', color: 'var(--accent)' },
      { label: 'Fetch rows',      color: 'var(--lime)' },
      { label: 'Cited answer',    color: '#B79DFF' },
    ],
    ctaText: 'Read the LangChain mesh-RAG recipe →',
    terminal: [
      { text: '# rag without a vector DB',              c: 'comment' },
      { text: '$ python agent.py',                      c: 'cmd' },
      { text: '> Which countries qualified for WC 2026?', c: 'url' },
      { text: '',                                       c: 'comment' },
      { text: '→ tool: instamesh_search(q="World Cup 2026 qualified")', c: 'step' },
      { text: '→ tool: fetch_grid_rows(showcase/world-cup-2026-qualified-teams)', c: 'step' },
      { text: '✓ answered with citation',               c: 'ok' },
      { text: '  source: showcase/world-cup-2026-qualified-teams · v1', c: 'url' },
    ],
  },
]

const TERM_COLORS: Record<TermColor, string> = {
  comment: 'var(--fg-4)',
  cmd:     'var(--fg-1)',
  step:    'var(--fg-2)',
  ok:      'var(--lime)',
  url:     'var(--accent)',
  vendor:  '#FF6C37',
}

const ROTATION_INTERVAL_MS = 6000
const FADE_MS = 220

function RotatingFeaturedRecipe() {
  const navigate = useNavigate()
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * HOMEPAGE_RECIPES.length))
  const [paused, setPaused] = useState(false)
  const [visible, setVisible] = useState(true)

  function go(delta: number) {
    setVisible(false)
    setTimeout(() => {
      setIdx(i => (i + delta + HOMEPAGE_RECIPES.length) % HOMEPAGE_RECIPES.length)
      setVisible(true)
    }, FADE_MS)
  }

  useEffect(() => {
    if (paused) return
    const id = setInterval(() => go(1), ROTATION_INTERVAL_MS)
    return () => clearInterval(id)
  }, [paused])

  const r = HOMEPAGE_RECIPES[idx]

  return (
    <div
      className="card"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        overflow: 'hidden',
        marginBottom: 16,
        background: `linear-gradient(135deg, ${r.vendorBg} 0%, transparent 60%), var(--bg-1)`,
        borderColor: r.vendorBorder,
        transition: 'border-color 220ms, background 220ms',
      }}
    >
      <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {r.showFirecrawlLogo ? <FirecrawlLogo size={14} /> : <VendorDot color={r.vendorColor} />}
          <span className="mono" style={{ fontSize: 11, color: r.vendorColor, fontWeight: 600 }}>{r.vendor}</span>
        </div>
        <span style={{ color: 'var(--fg-4)', fontSize: 11 }}>+</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>instadash</span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-4)', marginLeft: 4, letterSpacing: '0.08em' }}>FEATURED RECIPE</span>
        <span className="spacer" style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-4)' }}>{idx + 1} / {HOMEPAGE_RECIPES.length}</span>
        <button
          aria-label="previous recipe"
          onClick={() => go(-1)}
          className="mono"
          style={{ background: 'none', border: '1px solid var(--line-soft)', color: 'var(--fg-3)', cursor: 'pointer', padding: '2px 8px', borderRadius: 3, fontSize: 11 }}
        >‹</button>
        <button
          aria-label="next recipe"
          onClick={() => go(1)}
          className="mono"
          style={{ background: 'none', border: '1px solid var(--line-soft)', color: 'var(--fg-3)', cursor: 'pointer', padding: '2px 8px', borderRadius: 3, fontSize: 11 }}
        >›</button>
      </div>

      <div
        className="resp-grid-hero"
        style={{ gap: 0, alignItems: 'stretch', opacity: visible ? 1 : 0, transition: `opacity ${FADE_MS}ms ease` }}
      >
        <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' }}>{r.title}</div>
            <p style={{ marginTop: 10, fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.65, maxWidth: 360 }}>
              {r.blurb}
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 4 }}>
            {r.chain.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: step.color, whiteSpace: 'nowrap' }}>{step.label}</span>
                {i < r.chain.length - 1 && (
                  <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>→</span>
                )}
              </div>
            ))}
          </div>

          <button
            className="mono"
            onClick={() => navigate(`/recipes/${r.id}`)}
            style={{ marginTop: 'auto', fontSize: 11, color: r.vendorColor, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', paddingTop: 8 }}
          >
            {r.ctaText}
          </button>
        </div>

        <div style={{ borderLeft: '1px solid var(--line-soft)', background: 'var(--bg)', padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
            {r.showFirecrawlLogo ? <FirecrawlLogo size={12} /> : <VendorDot color={r.vendorColor} size={10} />}
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-4)', letterSpacing: '0.08em' }}>{r.vendorTag}</span>
          </div>
          <pre className="mono" style={{ fontSize: 12.5, lineHeight: 1.7, margin: 0, color: 'var(--fg-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {r.terminal.map((line, i) => (
              <div key={i} style={{ color: TERM_COLORS[line.c] }}>{line.text || ' '}</div>
            ))}
          </pre>
        </div>
      </div>
    </div>
  )
}

function VendorDot({ color, size = 12 }: { color: string; size?: number }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: 3,
      background: color,
      display: 'inline-block',
      flexShrink: 0,
    }} />
  )
}

function UseCases() {
  const navigate = useNavigate()

  const otherCases = [
    { tag: 'RESEARCH', title: 'Research agents',  body: 'Competitor research, source lists, pricing tables, market maps, extracted claims.',                recipe: '/recipes/firecrawl-to-instadash' },
    { tag: 'CODING',   title: 'Coding agents',    body: 'Logs, test results, API responses, migration output, debugging traces.',                            recipe: '/recipes/github-actions-nightly' },
    { tag: 'OPS',      title: 'Ops agents',       body: 'Incidents, queues, sync runs, error reports, webhooks, system snapshots.',                          recipe: '/recipes/stripe-webhooks-grid' },
    { tag: 'SALES',    title: 'Sales agents',     body: 'Prospect lists, account research, enrichment output, lead scoring, outreach queues.',               recipe: '/recipes/pydanticai-instadash' },
    { tag: 'SUPPORT',  title: 'Support agents',   body: 'Tickets, themes, product feedback, account issues, customer records.',                              recipe: '/recipes/csv-fastapi-upload' },
  ]

  return (
    <section className="section">
      <div className="container">
        <div className="eyebrow"><span className="num">05</span>use cases</div>
        <h2 className="h1" style={{ marginTop: 14 }}>Built for structured agent output.</h2>
        <p className="lede" style={{ marginTop: 12, marginBottom: 36, fontSize: 15, color: 'var(--fg-2)', maxWidth: 560 }}>
          Any agent that produces structured data has a home in Instadash.
        </p>

        <RotatingFeaturedRecipe />

        <div className="resp-grid-3" style={{ gap: 10 }}>
          {otherCases.map((c, i) => (
            <div key={i} style={{ background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 8, padding: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="mono" style={{ fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em' }}>{c.tag}</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{c.title}</div>
              <div className="muted" style={{ fontSize: 12, lineHeight: 1.55, flex: 1 }}>{c.body}</div>
              <button
                className="mono"
                onClick={() => navigate(c.recipe)}
                style={{ marginTop: 6, fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
              >
                Read the recipe →
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 18, textAlign: 'center' }}>
          <button
            className="mono"
            onClick={() => navigate('/recipes')}
            style={{ fontSize: 12, color: 'var(--fg-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            see all recipes →
          </button>
        </div>
      </div>
    </section>
  )
}



const LANDING_TIERS: PricingTier[] = [
  { name: 'Free',     price: '$0',     cadence: '/forever', desc: 'Try Instadash. Private by default, no credit card.',
    features: ['3 grids', '1k rows / grid', '7-day version history', 'CLI upload', 'Private by default'],
    cta: 'Start free' },
  { name: 'Builder',  price: '$19',    cadence: '/mo',      highlight: true, desc: 'For builders shipping real workflows.',
    features: ['Unlimited grids', 'Private grids', '100k rows / grid', '30-day version history', 'CSV, JSON, NDJSON, logs', 'API endpoints', 'LLM context files', 'Embeddable grids'],
    cta: 'Start Builder' },
  { name: 'Agent',    price: '$79',    cadence: '/mo',      desc: 'For agents, automations, and scheduled data flows.',
    features: ['Unlimited grids', '5M rows / grid', 'Hosted MCP server', 'Grid search API', 'Webhooks', 'Scheduled refresh', 'Team sharing', 'Higher API limits'],
    cta: 'Start Agent' },
  { name: 'Platform', price: 'Custom', cadence: '',         desc: 'For teams that need control over infra.',
    features: ['BYO Cloudflare account', 'Custom domain', 'SSO + audit logs', 'Private mesh sub-network', 'Self-hosted Worker', 'SLA'],
    cta: 'Contact sales' },
]

function PrivacySection() {
  const bullets = [
    'Private grids by default — nothing is public until you say so',
    'Public sharing when enabled — one toggle, one URL',
    'Read-only embeds — viewers can inspect, not edit',
    'API-key protected access — every request requires your key',
    'TTL support — set grids to auto-expire after a fixed period',
    'Delete any grid instantly — no retention after you remove it',
  ]
  return (
    <section className="section" style={{ borderBottom: '1px solid var(--line-soft)' }}>
      <div className="container">
        <div className="resp-grid-2" style={{ gap: 64, alignItems: 'center' }}>
          <div>
            <div className="eyebrow"><span className="num">07</span>private by default</div>
            <h2 className="h1" style={{ marginTop: 14 }}>Agent output should not leak.</h2>
            <p className="lede" style={{ marginTop: 16, fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.65 }}>
              Grids are private by default. Use Instadash for internal logs, research output, scraped data, and operational snapshots — without making every grid public by accident.
            </p>
          </div>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {bullets.map((b, i) => (
              <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.55 }}>
                <span style={{ color: 'var(--lime)', fontSize: 12, marginTop: 1 }}>✓</span>
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

function Pricing() {
  const navigate = useNavigate()
  return (
    <section className="section" id="pricing">
      <div className="container">
        <div className="eyebrow"><span className="num">08</span>pricing</div>
        <h2 className="h1" style={{ marginTop: 14 }}>Start free. Pay as you scale.</h2>
        <p className="lede" style={{ marginTop: 12, marginBottom: 36, fontSize: 15, color: 'var(--fg-2)', maxWidth: 560 }}>
          Free forever. Private by default. Upgrade for higher limits, longer retention, and agent workflows. No per-seat pricing.
        </p>

        <div className="resp-grid-4" style={{ gap: 14 }}>
          {LANDING_TIERS.map(t => (
            <PricingTierCard
              key={t.name}
              tier={t}
              ctaOverride={t.name !== 'Platform' ? `${t.cta} →` : t.cta}
              onSelect={() => navigate('/get-started')}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

function CtaStrip() {
  const navigate = useNavigate()
  return (
    <section style={{ padding: '72px 0', borderBottom: '1px solid var(--line-soft)' }}>
      <div className="container" style={{ textAlign: 'center' }}>
        <PipeRule label="ready" />
        <h2 className="h-display" style={{ fontSize: 'clamp(40px, 5vw, 64px)', marginTop: 32, marginBottom: 24 }}>
          Your agent has a <span className="accent">place to put its data.</span>
        </h2>
        <p className="lede" style={{ margin: '0 auto', textAlign: 'center', fontSize: 17 }}>
          Free to start. No database. No frontend. Just an endpoint for your agent.
        </p>
        <div className="row gap-12" style={{ justifyContent: 'center', marginTop: 32 }}>
          <button className="btn btn-primary btn-lg btn-mono" onClick={() => navigate('/get-started')}>npm i -g instadash</button>
          <button className="btn btn-ghost btn-lg" onClick={() => navigate('/docs')}>Browse docs</button>
        </div>
      </div>
    </section>
  )
}

export default function Landing() {
  return (
    <>
      <Hero />
      <ProblemStatement />
      <HowItWorks />
      <LiveDemo />
      <EmbedSection />
      <UseCases />
      <TwoPillars />
      <PrivacySection />
      <Pricing />
      <CtaStrip />
    </>
  )
}
